// src/pages/AdminSettings.jsx
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { useAuth } from "../context/AuthContext";
import { SECTIONS } from "../constants/sections";
import DashboardHeader from "../components/DashboardHeader";
import { api } from "../utils/api";

const SECTION_ICONS = ["📄","📚","💡","©️","🏅","🎓","💻","🏆","🧪","📱","💰","🎤","🎯","🔬","🌟","📊","🔭","🌐","🏛️","📝","🎖️","🔑","🏗️","📡"];
const SECTION_COLORS = [
  { color: "#4f8ef7", light: "#eaf1ff" },
  { color: "#7c3aed", light: "#f5f3ff" },
  { color: "#10b981", light: "#ecfdf5" },
  { color: "#f7a84f", light: "#fff5e6" },
  { color: "#e11d48", light: "#fff1f2" },
  { color: "#06b6d4", light: "#ecfeff" },
  { color: "#d97706", light: "#fffbeb" },
  { color: "#8b5cf6", light: "#f5f3ff" },
  { color: "#16a34a", light: "#f0fdf4" },
  { color: "#f74f6e", light: "#ffe6eb" },
];

const FIELD_TYPES = ["Text", "Number", "Date", "Month-Year", "Dropdown", "Textarea", "File", "Image"];

const mapRows = (res) =>
  Array.isArray(res)
    ? res.map((row) => ({
        id: row.id,
        ...(typeof row.data === "string" ? JSON.parse(row.data) : row.data || row),
      }))
    : [];

export default function AdminSettings() {
  const navigate = useNavigate();
  const { logout, userRole, currentUser } = useAuth();

  const isSuperAdmin = userRole === "superadmin";

  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "departments";
  const setActiveTab = (newTab) => setSearchParams({ tab: newTab });
  const [adminProfile, setAdminProfile] = useState(null);
  const [activeSectionKey, setActiveSectionKey] = useState(SECTIONS[0].key);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState("");

  // Custom sections (superadmin managed)
  const [customSections, setCustomSections] = useState([]);
  const [allSections, setAllSections] = useState(SECTIONS);
  const [newSection, setNewSection] = useState({ label: "", icon: "📄", colorIndex: 0 });
  const [editingSection, setEditingSection] = useState(null); // { id, label, icon, colorIndex }

  // Departments & Colleges
  const [departments, setDepartments] = useState([]);
  const [colleges, setColleges] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [newDept, setNewDept] = useState("");
  const [newCollege, setNewCollege] = useState("");
  const [newDesignation, setNewDesignation] = useState("");
  const [editingDept, setEditingDept] = useState(null);     // { id, name }
  const [editingCollege, setEditingCollege] = useState(null); // { id, name }
  const [editingDesignation, setEditingDesignation] = useState(null); // { id, name }

  // Education Additions
  const [eduCourses, setEduCourses] = useState([]);
  const [eduDegrees, setEduDegrees] = useState([]);
  const [eduStreams, setEduStreams] = useState([]);
  const [eduBoards, setEduBoards] = useState([]);
  const [eduStateCouncils, setEduStateCouncils] = useState([]);

  // Section Fields
  const [sectionFields, setSectionFields] = useState({});
  const [draggedSectionFields, setDraggedSectionFields] = useState(null); // Track current drag state for active section
  const [hasSectionFieldsChanged, setHasSectionFieldsChanged] = useState(false); // Track if order changed

  const [draggedFacultyFields, setDraggedFacultyFields] = useState(null); // Track current drag state for faculty
  const [hasFacultyFieldsChanged, setHasFacultyFieldsChanged] = useState(false); // Track if order changed

  // Print Settings / Letterhead
  const [letterhead, setLetterhead] = useState({
    collegeName: "ADHIPARASAKTHI DENTAL COLLEGE & HOSPITAL",
    address: "MELMARUVATHUR, TAMIL NADU - 603319",
    dciTitle: "Staff DCI Report",
    tnmgrTitle: "Staff Details for TNMGR University",
    phone: "",
    email: "",
  });
  const [savingLetterhead, setSavingLetterhead] = useState(false);;
  const [newField, setNewField] = useState({ name: "", type: "Text", required: false, options: "" });
  const [editingField, setEditingField] = useState(null); // { id, name, type, required, options }

  // Faculty Profile Fields
  const [facultyFields, setFacultyFields] = useState([]);
  const [newFacultyField, setNewFacultyField] = useState({ name: "", type: "Text", required: false, options: "", group: "" });
  const [editingFacultyField, setEditingFacultyField] = useState(null);

  useEffect(() => { 
    fetchAll(); 
    fetchAdminProfile();
  }, []);

  useEffect(() => {
    if (activeTab === "fields" && allSections.length > 0 && Object.keys(sectionFields).length === 0) {
      loadSectionFieldsLazily();
    }
  }, [activeTab, allSections]);

  async function loadSectionFieldsLazily() {
    try {
      const allFields = {};
      
      // Fetch fields from backend API for each section
      const sectionPromises = allSections.map(sec => 
        api.get(`/section_fields_${sec.key}?orderBy=order`)
          .catch(e => {
            console.warn(`Failed to load fields for ${sec.key}, falling back to empty:`, e);
            return [];
          })
      );
      
      const sectionResults = await Promise.all(sectionPromises);
      allSections.forEach((sec, i) => {
        const fields = sectionResults[i];
        // Ensure fields are sorted by order
        if (Array.isArray(fields)) {
          allFields[sec.key] = fields.sort((a, b) => (a.order || 0) - (b.order || 0));
        } else {
          allFields[sec.key] = [];
        }
      });
      setSectionFields(allFields);
    } catch (e) {
      console.error("Failed to lazy load section fields", e);
    }
  }

  async function fetchAdminProfile() {
    try {
      if (currentUser?.uid) {
        const res = await api.get(`/faculty/${currentUser.uid}/profile`);
        if (res) setAdminProfile({ id: res.id, ...(res.data || res) });
      }
    } catch {}
  }

  async function fetchAll() {
    setLoading(true);
    try {
      const fetchListNoOrder = async (coll) => {
        const res = await api.get(`/${coll}`);
        return mapRows(res).sort((a, b) => a.name?.localeCompare(b.name));
      };

      const [dRes, cRes, desRes, eduCoursesData, eduDegreesData, eduStreamsData, eduBoardsData, eduStateCouncilsData, lhRes, csRes, ffRes] = await Promise.all([
        api.get("/departments?orderBy=name"),
        api.get("/colleges?orderBy=name"),
        api.get("/designations"),
        fetchListNoOrder("edu_courses"),
        fetchListNoOrder("edu_degrees"),
        fetchListNoOrder("edu_streams"),
        fetchListNoOrder("edu_boards"),
        fetchListNoOrder("edu_state_councils"),
        api.get("/settings/letterhead").catch(() => null),
        api.get("/custom_sections?orderBy=createdAt"),
        api.get("/faculty_profile_fields?orderBy=order"),
      ]);

      setDepartments(mapRows(dRes));
      setColleges(mapRows(cRes));
      setDesignations(mapRows(desRes).sort((a, b) => a.name?.localeCompare(b.name)));

      setEduCourses(eduCoursesData);
      setEduDegrees(eduDegreesData);
      setEduStreams(eduStreamsData);
      setEduBoards(eduBoardsData);
      setEduStateCouncils(eduStateCouncilsData);

      if (lhRes) setLetterhead((prev) => ({ ...prev, ...(lhRes.data || lhRes) }));

      const custom = mapRows(csRes);
      setCustomSections(custom);
      const merged = [...SECTIONS, ...custom.filter(c => !SECTIONS.find(s => s.key === c.key))];
      setAllSections(merged);
      if (!merged.find(s => s.key === activeSectionKey)) {
        setActiveSectionKey(merged[0]?.key || SECTIONS[0].key);
      }

      // Skip loading section fields here to avoid freezing the UI on mount
      // They will be loaded lazily by a separate useEffect when activeTab === "fields"

      let ffList = mapRows(ffRes);

      if (ffList.length === 0) {
        const builtIn = [
          { name: "Full Name", key: "name", type: "Text", required: true, order: 1, isSystem: true, group: "Staff Details" },
          { name: "Mobile", key: "mobile", type: "Text", required: true, order: 2, isSystem: true, group: "Staff Details" },
          { name: "Employee ID", key: "empId", type: "Text", required: true, order: 3, isSystem: true, group: "Staff Details" },
          { name: "Department", key: "department", type: "Dropdown", required: true, order: 4, isSystem: true, group: "Job Details" },
          { name: "Designation", key: "designation", type: "Dropdown", required: false, order: 5, isSystem: true, group: "Job Details" },
          { name: "College", key: "institution", type: "Dropdown", required: true, order: 6, isSystem: true, group: "Job Details" },
        ];
        await Promise.all(
          builtIn.map((f) =>
            api.post("/faculty_profile_fields", { ...f, createdAt: new Date().toISOString() })
          )
        );
        ffList = mapRows(await api.get("/faculty_profile_fields?orderBy=order"));
      }
      setFacultyFields(ffList);
    } catch (err) { console.error(err); }
    setLoading(false);
  }

  function showToast(msg) { setToast(msg); setTimeout(() => setToast(""), 3000); }

  // Department CRUD
  async function addDepartment() {
    if (!newDept.trim()) return;
    try {
      await api.post("/departments", { name: newDept.trim(), createdAt: new Date().toISOString() });
      setNewDept(""); fetchAll(); showToast("✅ Department added");
    } catch { showToast("❌ Failed"); }
  }
  async function syncDepartmentsFromFaculty() {
    setLoading(true);
    try {
      const users = mapRows(await api.get("/users"));
      const depts = new Set();
      users.forEach((fp) => {
        if (fp.department) depts.add(fp.department.trim());
      });
      const existing = mapRows(await api.get("/departments")).map((d) => d.name?.trim());

      let added = 0;
      for (const dept of depts) {
        if (!existing.includes(dept)) {
          await api.post("/departments", { name: dept, createdAt: new Date().toISOString() });
          added++;
        }
      }
      fetchAll();
      showToast(`✅ Synced ${added} new departments!`);
    } catch (e) {
      console.error(e);
      showToast("❌ Failed to sync departments");
    }
    setLoading(false);
  }

  async function removeDuplicateDepartments() {
    if (!window.confirm("This will remove duplicate departments (case-insensitive). Continue?")) return;
    setLoading(true);
    try {
      // Fetch all faculty to know which dept names are actually in use
      const usersSnap = mapRows(await api.get("/users"));
      const usedDeptNames = new Set(
        usersSnap.map((d) => (d.department || "").trim().toUpperCase()).filter(Boolean)
      );

      const allDepts = mapRows(await api.get("/departments")).map((d) => ({
        id: d.id,
        name: (d.name || "").trim(),
      }));

      // Group by uppercase name
      const groups = {};
      for (const dept of allDepts) {
        const key = dept.name.toUpperCase();
        if (!groups[key]) groups[key] = [];
        groups[key].push(dept);
      }

      let removed = 0;
      for (const [key, group] of Object.entries(groups)) {
        if (group.length <= 1) continue;
        // Prefer the one whose name matches faculty records (exact match by uppercase)
        // Keep the one assigned to faculty; if none, keep the first (alphabetically by name length/case)
        const inUse = group.find(d => usedDeptNames.has(d.name.toUpperCase()));
        const keeper = inUse || group[0];
        const toDelete = group.filter(d => d.id !== keeper.id);
        for (const dup of toDelete) {
          await api.delete(`/departments/${dup.id}`);
          removed++;
        }
      }
      fetchAll();
      showToast(removed > 0 ? `✅ Removed ${removed} duplicate(s)!` : "✅ No duplicates found");
    } catch (e) {
      console.error(e);
      showToast("❌ Failed to remove duplicates");
    }
    setLoading(false);
  }

  async function deleteDepartment(id, name) {
    if (!window.confirm("Delete this department?")) return;
    try {
      const users = mapRows(await api.get("/users"));
      if (users.some((u) => u.department === name)) {
        return showToast("❌ Cannot delete: Department is assigned to faculty");
      }
      await api.delete(`/departments/${id}`); fetchAll(); showToast("✅ Deleted"); 
    }
    catch { showToast("❌ Failed"); }
  }
  async function updateDepartment() {
    if (!editingDept?.name?.trim()) return;
    try {
      await api.put(`/departments/${editingDept.id}`, { name: editingDept.name.trim() });
      setEditingDept(null); fetchAll(); showToast("✅ Department updated");
    } catch { showToast("❌ Failed"); }
  }

  // College CRUD
  async function addCollege() {
    if (!newCollege.trim()) return;
    try {
      await api.post("/colleges", { name: newCollege.trim(), createdAt: new Date().toISOString() });
      setNewCollege(""); fetchAll(); showToast("✅ College added");
    } catch { showToast("❌ Failed"); }
  }
  async function deleteCollege(id) {
    if (!window.confirm("Delete this college?")) return;
    try { await api.delete(`/colleges/${id}`); fetchAll(); showToast("✅ Deleted"); }
    catch { showToast("❌ Failed"); }
  }
  async function updateCollege() {
    if (!editingCollege?.name?.trim()) return;
    try {
      await api.put(`/colleges/${editingCollege.id}`, { name: editingCollege.name.trim() });
      setEditingCollege(null); fetchAll(); showToast("✅ College updated");
    } catch { showToast("❌ Failed"); }
  }

  async function addDesignation() {
    if (!newDesignation.trim()) return;
    try {
      await api.post("/designations", { name: newDesignation.trim(), createdAt: new Date().toISOString() });
      setNewDesignation(""); fetchAll(); showToast("✅ Designation added");
    } catch { showToast("❌ Failed"); }
  }
  async function deleteDesignation(id) {
    if (!window.confirm("Delete this designation?")) return;
    try { await api.delete(`/designations/${id}`); fetchAll(); showToast("✅ Deleted"); }
    catch { showToast("❌ Failed"); }
  }
  async function updateDesignation() {
    if (!editingDesignation?.name?.trim()) return;
    try {
      await api.put(`/designations/${editingDesignation.id}`, { name: editingDesignation.name.trim() });
      setEditingDesignation(null); fetchAll(); showToast("✅ Designation updated");
    } catch { showToast("❌ Failed"); }
  }

  // Section Field CRUD
  async function addField() {
    if (!newField.name.trim()) return showToast("❌ Field name required");
    const fields = sectionFields[activeSectionKey] || [];
    const maxOrder = fields.reduce((m, f) => Math.max(m, f.order ?? 0), fields.length);
    try {
      const fieldData = {
        name: newField.name.trim(),
        type: newField.type,
        required: newField.required,
        order: maxOrder + 1,
        createdAt: new Date().toISOString(),
      };
      if (newField.type === "Dropdown" && newField.options.trim()) {
        fieldData.options = newField.options.split(",").map(o => o.trim()).filter(Boolean);
      }
      await api.post(`/section_fields_${activeSectionKey}`, fieldData);
      setNewField({ name: "", type: "Text", required: false, options: "" });
      fetchAll();
      showToast("✅ Field added successfully");
    } catch { showToast("❌ Failed to add field"); }
  }

  async function deleteField(fieldId) {
    if (!window.confirm("Delete this field?")) return;
    try {
      await api.delete(`/section_fields_${activeSectionKey}/${fieldId}`);
      fetchAll();
      showToast("✅ Field deleted");
    } catch { showToast("❌ Failed"); }
  }

  async function saveEditField() {
    if (!editingField.name.trim()) return showToast("❌ Field name required");
    try {
      const updateData = {
        name: editingField.name.trim(),
        type: editingField.type,
        required: editingField.required,
      };
      if (editingField.type === "Dropdown" && editingField.options) {
        updateData.options = typeof editingField.options === "string"
          ? editingField.options.split(",").map(o => o.trim()).filter(Boolean)
          : editingField.options;
      } else {
        updateData.options = [];
      }
      await api.put(`/section_fields_${activeSectionKey}/${editingField.id}`, updateData);
      setEditingField(null);
      fetchAll();
      showToast("✅ Field updated successfully");
    } catch { showToast("❌ Failed to update"); }
  }

  async function handleDragEndSectionFields(result) {
    const { source, destination } = result;
    
    // Dropped outside the list
    if (!destination) return;
    
    // No change in position
    if (source.index === destination.index) return;
    
    const fields = sectionFields[activeSectionKey] || [];
    const newFields = Array.from(fields);
    const [movedField] = newFields.splice(source.index, 1);
    newFields.splice(destination.index, 0, movedField);
    
    // Reassign order values to reflect new position
    const reordered = newFields.map((field, index) => ({
      ...field,
      order: index + 1
    }));
    
    // Update state with new order
    setSectionFields(prev => ({
      ...prev,
      [activeSectionKey]: reordered
    }));
    
    // Mark as changed
    setHasSectionFieldsChanged(true);
    setDraggedSectionFields(null);
  }

  async function handleDragEndFacultyFields(result) {
    const { source, destination } = result;
    
    // Dropped outside the list
    if (!destination) return;
    
    // No change in position
    if (source.index === destination.index) return;
    
    const newFields = Array.from(facultyFields);
    const [movedField] = newFields.splice(source.index, 1);
    newFields.splice(destination.index, 0, movedField);
    
    // Reassign order values to reflect new position
    const reordered = newFields.map((field, index) => ({
      ...field,
      order: index + 1
    }));
    
    // Update state with new order
    setFacultyFields(reordered);
    
    // Mark as changed
    setHasFacultyFieldsChanged(true);
    setDraggedFacultyFields(null);
  }

  async function saveSectionFieldsOrder() {
    const fields = sectionFields[activeSectionKey] || [];
    if (fields.length === 0) return;
    
    showToast("⏳ Saving order...");
    
    try {
      // Create payload with id and new order values
      const payload = {
        fields: fields.map((f, index) => ({
          id: f.id,
          order: index + 1
        }))
      };
      
      console.log(`[saveSectionFieldsOrder] Sending reorder payload:`, payload);
      console.log(`[saveSectionFieldsOrder] API endpoint: /reorder/${activeSectionKey}`);
      
      // Call batch reorder endpoint (new route: /api/reorder/:table)
      const result = await api.post(`/reorder/section_fields_${activeSectionKey}`, payload);
      
      console.log(`[saveSectionFieldsOrder] Result:`, result);
      
      if (result.success) {
        showToast(`✅ Order saved! (${result.updated}/${result.total} fields updated)`);
        setHasSectionFieldsChanged(false);
        
        // Refresh the section fields to verify
        const refreshed = await api.get(`/section_fields_${activeSectionKey}?orderBy=order`);
        const sorted = (refreshed || []).sort((a, b) => (a.order || 0) - (b.order || 0));
        setSectionFields(prev => ({
          ...prev,
          [activeSectionKey]: sorted
        }));
      } else {
        showToast("❌ Order save failed: " + (result.error || "Unknown error"));
      }
    } catch (e) {
      console.error("❌ Failed to save order:", e.message, e);
      showToast("❌ Failed to save order: " + e.message);
    }
  }

  async function saveFacultyFieldsOrder() {
    if (facultyFields.length === 0) return;
    
    showToast("⏳ Saving order...");
    
    try {
      // Create payload with id and new order values
      const payload = {
        fields: facultyFields.map((f, index) => ({
          id: f.id,
          order: index + 1
        }))
      };
      
      console.log(`[saveFacultyFieldsOrder] Sending reorder payload:`, payload);
      console.log(`[saveFacultyFieldsOrder] API endpoint: /reorder/faculty_profile_fields`);
      
      // Call batch reorder endpoint (new route: /api/reorder/:table)
      const result = await api.post(`/reorder/faculty_profile_fields`, payload);
      
      console.log(`[saveFacultyFieldsOrder] Result:`, result);
      
      if (result.success) {
        showToast(`✅ Order saved! (${result.updated}/${result.total} fields updated)`);
        setHasFacultyFieldsChanged(false);
        
        // Refresh the faculty fields to verify
        const refreshed = await api.get(`/faculty_profile_fields?orderBy=order`);
        const sorted = (refreshed || []).sort((a, b) => (a.order || 0) - (b.order || 0));
        setFacultyFields(sorted);
      } else {
        showToast("❌ Order save failed: " + (result.error || "Unknown error"));
      }
    } catch (e) {
      console.error("❌ Failed to save order:", e.message, e);
      showToast("❌ Failed to save order: " + e.message);
    }
  }
  
  async function handleLogout() { await logout(); navigate("/admin"); }

  // Custom Section CRUD
  async function addCustomSection() {
    const label = newSection.label.trim();
    if (!label) return showToast("❌ Section name required");
    // Generate a camelCase key from label
    const key = label.toLowerCase().replace(/[^a-z0-9 ]/g, "").trim().replace(/ +/g, "_").replace(/_+(\w)/g, (_, c) => c.toUpperCase()).replace(/^(\w)/, (_, c) => c.toLowerCase());
    if (SECTIONS.find(s => s.key === key) || customSections.find(s => s.key === key)) {
      return showToast("❌ Section with this name already exists");
    }
    const colorData = SECTION_COLORS[newSection.colorIndex % SECTION_COLORS.length];
    try {
      await api.post("/custom_sections", {
        key,
        label,
        icon: newSection.icon,
        color: colorData.color,
        light: colorData.light,
        createdAt: new Date().toISOString(),
      });
      setNewSection({ label: "", icon: "📄", colorIndex: 0 });
      fetchAll();
      showToast("✅ Section created successfully");
    } catch { showToast("❌ Failed to create section"); }
  }

  async function deleteCustomSection(id, key) {
    if (!window.confirm("Delete this section? All field definitions for it will also be removed.")) return;
    try {
      await api.delete(`/custom_sections/${id}`);
      const fieldsSnap = mapRows(await api.get(`/section_fields_${key}`));
      await Promise.all(fieldsSnap.map((d) => api.delete(`/section_fields_${key}/${d.id}`)));
      fetchAll();
      showToast("✅ Section deleted");
    } catch { showToast("❌ Failed to delete"); }
  }

  async function updateCustomSection() {
    if (!editingSection?.label?.trim()) return;
    try {
      const colors = SECTION_COLORS[editingSection.colorIndex % SECTION_COLORS.length];
      await api.put(`/custom_sections/${editingSection.id}`, {
        label: editingSection.label.trim(),
        icon: editingSection.icon,
        color: colors.color,
        light: colors.light,
      });
      setEditingSection(null); fetchAll(); showToast("✅ Section updated");
    } catch (err) { console.error(err); showToast("❌ Failed to update section"); }
  }

  // Faculty Profile Field CRUD
  async function addFacultyField() {
    if (!newFacultyField.name.trim()) return showToast("❌ Field name required");
    const maxOrder = facultyFields.reduce((m, f) => Math.max(m, f.order ?? 0), facultyFields.length);
    try {
      const fieldData = {
        name: newFacultyField.name.trim(),
        type: newFacultyField.type,
        required: newFacultyField.required,
        group: newFacultyField.group.trim(),
        order: maxOrder + 1,
        createdAt: new Date().toISOString(),
      };
      if (newFacultyField.type === "Dropdown" && newFacultyField.options.trim()) {
        fieldData.options = newFacultyField.options.split(",").map(o => o.trim()).filter(Boolean);
      }
      await api.post("/faculty_profile_fields", fieldData);
      setNewFacultyField({ name: "", type: "Text", required: false, options: "", group: "" });
      fetchAll();
      showToast("✅ Faculty field added");
    } catch { showToast("❌ Failed to add faculty field"); }
  }

  async function deleteFacultyField(fieldId) {
    if (!window.confirm("Delete this profile field?")) return;
    try {
      await api.delete(`/faculty_profile_fields/${fieldId}`);
      fetchAll();
      showToast("✅ Field deleted");
    } catch { showToast("❌ Failed to delete"); }
  }

  async function saveEditFacultyField() {
    if (!editingFacultyField.name.trim()) return showToast("❌ Field name required");
    try {
      const updateData = {
        name: editingFacultyField.name.trim(),
        type: editingFacultyField.type,
        required: editingFacultyField.required,
        group: editingFacultyField.group?.trim() || "",
      };
      if (editingFacultyField.type === "Dropdown" && editingFacultyField.options) {
        updateData.options = typeof editingFacultyField.options === "string"
          ? editingFacultyField.options.split(",").map(o => o.trim()).filter(Boolean)
          : editingFacultyField.options;
      } else {
        updateData.options = [];
      }
      await api.put(`/faculty_profile_fields/${editingFacultyField.id}`, updateData);
      setEditingFacultyField(null);
      fetchAll();
      showToast("✅ Updated successfully");
    } catch { showToast("❌ Failed to update"); }
  }

  const hasRight = (key) => adminProfile?.role === "superadmin" || (adminProfile?.rights || []).includes(key);

  const mainTabs = [
    { key: "departments", label: "🏢 Departments", show: true },
    { key: "colleges", label: "🎓 Colleges", show: true },
    { key: "designations", label: "🎖️ Designations", show: hasRight("designation") },
    { key: "education", label: "🎓 Education Options", show: true },
    { key: "fields", label: "📋 Section Fields", show: hasRight("section_fields") },
    { key: "facultyFields", label: "👤 Faculty Fields", show: hasRight("faculty_fields") },
    { key: "sections", label: "➕ Manage Sections", show: hasRight("sections") },
    { key: "printSettings", label: "🖨️ Print Settings", show: true },
  ].filter(t => t.show);

  const inputStyle = { flex: 1, background: "#f8fafc", border: "1.5px solid #e2e8f0", borderRadius: 10, padding: "10px 14px", fontSize: 15, outline: "none", fontFamily: "Georgia, serif", color: "#1e293b", boxSizing: "border-box" };
  const labelStyle = { fontSize: 13, fontWeight: 700, color: "#374151", display: "block", marginBottom: 5 };
  const currentFields = sectionFields[activeSectionKey] || [];
  const activeSectionData = allSections.find(s => s.key === activeSectionKey);

  return (
    <div style={{ minHeight: "100vh", background: "#f0f4ff", fontFamily: "Georgia, serif" }}>

      <DashboardHeader label="ADMIN">
        <button onClick={() => navigate("/admin/dashboard")} style={{ background: "#fff", color: "#1d4ed8", border: "none", borderRadius: 10, padding: "8px 18px", fontWeight: 800, fontSize: 16, cursor: "pointer" }}>← Dashboard</button>
        <button onClick={handleLogout} style={{ background: "rgba(255,255,255,0.15)", color: "#fff", border: "1px solid rgba(255,255,255,0.3)", borderRadius: 10, padding: "8px 16px", fontWeight: 600, fontSize: 15, cursor: "pointer" }}>🚪 Logout</button>
      </DashboardHeader>

      {/* Toast */}
      {toast && (
        <div style={{ position: "fixed", top: 80, right: 24, zIndex: 999, background: toast.startsWith("✅") ? "#f0fdf4" : "#fef2f2", border: `1px solid ${toast.startsWith("✅") ? "#86efac" : "#fecaca"}`, color: toast.startsWith("✅") ? "#16a34a" : "#dc2626", borderRadius: 12, padding: "14px 20px", fontWeight: 700, fontSize: 16, boxShadow: "0 4px 20px rgba(0,0,0,0.1)" }}>
          {toast}
        </div>
      )}

      <div style={{ padding: "28px 32px", maxWidth: 1000, margin: "0 auto" }}>
        <h1 style={{ fontSize: 26, fontWeight: 900, color: "#0f2557", marginBottom: 6 }}>⚙️ Settings</h1>
        <p style={{ color: "#64748b", marginBottom: 24 }}>Manage departments, colleges, and input fields for each section.</p>

        {/* Main Tabs */}
        <div style={{ display: "flex", gap: 8, marginBottom: 24 }}>
          {mainTabs.map(t => (
            <button key={t.key} onClick={() => setActiveTab(t.key)}
              style={{ padding: "10px 22px", borderRadius: 10, border: "none", fontWeight: 700, fontSize: 16, cursor: "pointer", background: activeTab === t.key ? "#2563eb" : "#fff", color: activeTab === t.key ? "#fff" : "#475569", boxShadow: "0 2px 8px rgba(15,37,87,0.08)" }}>
              {t.label}
            </button>
          ))}
        </div>

        {/* ── DEPARTMENTS ── */}
        {activeTab === "departments" && (
          <div style={{ background: "#fff", borderRadius: 20, boxShadow: "0 2px 16px rgba(15,37,87,0.08)", overflow: "hidden" }}>
            <div style={{ padding: "22px 28px", borderBottom: "1px solid #f1f5f9" }}>
              <h2 style={{ fontWeight: 800, fontSize: 18, color: "#0f2557", marginBottom: 14 }}>Add New Department</h2>
              <div style={{ display: "flex", gap: 12 }}>
                <input value={newDept} onChange={e => setNewDept(e.target.value)} onKeyDown={e => e.key === "Enter" && addDepartment()} placeholder="e.g. Conservative Dentistry, Orthodontics..." style={inputStyle} />
                <button onClick={addDepartment} style={{ background: "linear-gradient(135deg,#1d4ed8,#2563eb)", color: "#fff", border: "none", borderRadius: 10, padding: "10px 24px", fontWeight: 800, fontSize: 16, cursor: "pointer" }}>+ Add</button>
                <button onClick={syncDepartmentsFromFaculty} style={{ background: "#10b981", color: "#fff", border: "none", borderRadius: 10, padding: "10px 24px", fontWeight: 800, fontSize: 16, cursor: "pointer", whiteSpace: "nowrap" }}>🔄 Sync from Faculty</button>
                <button onClick={removeDuplicateDepartments} style={{ background: "#f59e0b", color: "#fff", border: "none", borderRadius: 10, padding: "10px 24px", fontWeight: 800, fontSize: 16, cursor: "pointer", whiteSpace: "nowrap" }}>🧹 Remove Duplicates</button>
              </div>
            </div>
            {loading ? <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>⏳ Loading...</div>
              : departments.length === 0 ? <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>No departments yet.</div>
              : departments.map((d, i) => (
                <div key={d.id} style={{ borderBottom: i < departments.length - 1 ? "1px solid #f8fafc" : "none" }}>
                  {editingDept?.id === d.id ? (
                    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 28px", background: "#f0f9ff", borderLeft: "3px solid #2563eb" }}>
                      <div style={{ width: 36, height: 36, background: "#eaf1ff", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>🏢</div>
                      <input
                        value={editingDept.name}
                        onChange={e => setEditingDept(prev => ({ ...prev, name: e.target.value }))}
                        onKeyDown={e => { if (e.key === "Enter") updateDepartment(); if (e.key === "Escape") setEditingDept(null); }}
                        autoFocus
                        style={{ flex: 1, background: "#fff", border: "1.5px solid #93c5fd", borderRadius: 8, padding: "8px 12px", fontSize: 15, outline: "none", fontFamily: "Georgia, serif" }} />
                      <button onClick={updateDepartment} style={{ background: "linear-gradient(135deg,#1d4ed8,#2563eb)", color: "#fff", border: "none", borderRadius: 8, padding: "8px 16px", fontWeight: 700, cursor: "pointer", fontSize: 15, whiteSpace: "nowrap" }}>✅ Save</button>
                      <button onClick={() => setEditingDept(null)} style={{ background: "#f1f5f9", color: "#475569", border: "none", borderRadius: 8, padding: "8px 12px", fontWeight: 700, cursor: "pointer", fontSize: 15 }}>✕</button>
                    </div>
                  ) : (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "13px 28px", transition: "background 0.15s" }}
                      onMouseEnter={e => e.currentTarget.style.background = "#f8fafc"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <div style={{ width: 36, height: 36, background: "#eaf1ff", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center" }}>🏢</div>
                        <span style={{ fontWeight: 600, color: "#1e293b", fontSize: 16 }}>{d.name}</span>
                      </div>
                      <div style={{ display: "flex", gap: 8 }}>
                        <button onClick={() => setEditingDept({ id: d.id, name: d.name })} style={{ background: "#eaf1ff", color: "#2563eb", border: "none", borderRadius: 8, padding: "6px 14px", fontWeight: 700, cursor: "pointer", fontSize: 15 }}>✏️ Edit</button>
                        <button onClick={() => deleteDepartment(d.id, d.name)} style={{ background: "#fef2f2", color: "#dc2626", border: "none", borderRadius: 8, padding: "6px 14px", fontWeight: 700, cursor: "pointer", fontSize: 15 }}>🗑 Delete</button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
          </div>
        )}

        {/* ── COLLEGES ── */}
        {activeTab === "colleges" && (
          <div style={{ background: "#fff", borderRadius: 20, boxShadow: "0 2px 16px rgba(15,37,87,0.08)", overflow: "hidden" }}>
            <div style={{ padding: "22px 28px", borderBottom: "1px solid #f1f5f9" }}>
              <h2 style={{ fontWeight: 800, fontSize: 18, color: "#0f2557", marginBottom: 4 }}>Add New College</h2>
              <p style={{ fontSize: 14, color: "#94a3b8", marginBottom: 14 }}>Adhiparasakthi Dental College and Hospital is the default.</p>
              <div style={{ display: "flex", gap: 12 }}>
                <input value={newCollege} onChange={e => setNewCollege(e.target.value)} onKeyDown={e => e.key === "Enter" && addCollege()} placeholder="e.g. Adhiparasakthi College of Arts..." style={inputStyle} />
                <button onClick={addCollege} style={{ background: "linear-gradient(135deg,#1d4ed8,#2563eb)", color: "#fff", border: "none", borderRadius: 10, padding: "10px 24px", fontWeight: 800, fontSize: 16, cursor: "pointer" }}>+ Add</button>
              </div>
            </div>
            {/* Default */}
            <div style={{ display: "flex", alignItems: "center", padding: "13px 28px", background: "#f0fdf4", borderBottom: "1px solid #f1f5f9" }}>
              <div style={{ width: 36, height: 36, background: "#dcfce7", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", marginRight: 12 }}>🎓</div>
              <span style={{ fontWeight: 700, color: "#1e293b", fontSize: 16 }}>Adhiparasakthi Dental College and Hospital</span>
              <span style={{ background: "#dcfce7", color: "#16a34a", borderRadius: 20, padding: "1px 10px", fontSize: 13, fontWeight: 700, marginLeft: 10 }}>DEFAULT</span>
            </div>
            {colleges.map((c, i) => (
              <div key={c.id} style={{ borderBottom: i < colleges.length - 1 ? "1px solid #f8fafc" : "none" }}>
                {editingCollege?.id === c.id ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 28px", background: "#f0f9ff", borderLeft: "3px solid #2563eb" }}>
                    <div style={{ width: 36, height: 36, background: "#eaf1ff", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>🎓</div>
                    <input
                      value={editingCollege.name}
                      onChange={e => setEditingCollege(prev => ({ ...prev, name: e.target.value }))}
                      onKeyDown={e => { if (e.key === "Enter") updateCollege(); if (e.key === "Escape") setEditingCollege(null); }}
                      autoFocus
                      style={{ flex: 1, background: "#fff", border: "1.5px solid #93c5fd", borderRadius: 8, padding: "8px 12px", fontSize: 15, outline: "none", fontFamily: "Georgia, serif" }} />
                    <button onClick={updateCollege} style={{ background: "linear-gradient(135deg,#1d4ed8,#2563eb)", color: "#fff", border: "none", borderRadius: 8, padding: "8px 16px", fontWeight: 700, cursor: "pointer", fontSize: 15, whiteSpace: "nowrap" }}>✅ Save</button>
                    <button onClick={() => setEditingCollege(null)} style={{ background: "#f1f5f9", color: "#475569", border: "none", borderRadius: 8, padding: "8px 12px", fontWeight: 700, cursor: "pointer", fontSize: 15 }}>✕</button>
                  </div>
                ) : (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "13px 28px", transition: "background 0.15s" }}
                    onMouseEnter={e => e.currentTarget.style.background = "#f8fafc"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <div style={{ width: 36, height: 36, background: "#eaf1ff", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center" }}>🎓</div>
                      <span style={{ fontWeight: 600, color: "#1e293b", fontSize: 16 }}>{c.name}</span>
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button onClick={() => setEditingCollege({ id: c.id, name: c.name })} style={{ background: "#eaf1ff", color: "#2563eb", border: "none", borderRadius: 8, padding: "6px 14px", fontWeight: 700, cursor: "pointer", fontSize: 15 }}>✏️ Edit</button>
                      <button onClick={() => deleteCollege(c.id)} style={{ background: "#fef2f2", color: "#dc2626", border: "none", borderRadius: 8, padding: "6px 14px", fontWeight: 700, cursor: "pointer", fontSize: 15 }}>🗑 Delete</button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* ── PRINT SETTINGS ── */}
        {activeTab === "printSettings" && (
          <div style={{ background: "#fff", borderRadius: 20, boxShadow: "0 2px 16px rgba(15,37,87,0.08)", padding: 32 }}>
            <h2 style={{ fontWeight: 800, fontSize: 18, color: "#0f2557", marginBottom: 4 }}>🖨️ Print Letterhead Settings</h2>
            <p style={{ fontSize: 14, color: "#94a3b8", marginBottom: 24 }}>These details appear on the printed letterhead of DCI and TNMGR reports.</p>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
              <div>
                <label style={labelStyle}>College Name (Letterhead)</label>
                <input value={letterhead.collegeName} onChange={e => setLetterhead(p => ({ ...p, collegeName: e.target.value }))} style={inputStyle} placeholder="e.g. ADHIPARASAKTHI DENTAL COLLEGE" />
              </div>
              <div>
                <label style={labelStyle}>Address / Tagline</label>
                <input value={letterhead.address} onChange={e => setLetterhead(p => ({ ...p, address: e.target.value }))} style={inputStyle} placeholder="e.g. MELMARUVATHUR, TAMIL NADU - 603319" />
              </div>
              <div>
                <label style={labelStyle}>Phone</label>
                <input value={letterhead.phone} onChange={e => setLetterhead(p => ({ ...p, phone: e.target.value }))} style={inputStyle} placeholder="e.g. 044-27512345" />
              </div>
              <div>
                <label style={labelStyle}>Email</label>
                <input value={letterhead.email} onChange={e => setLetterhead(p => ({ ...p, email: e.target.value }))} style={inputStyle} placeholder="e.g. principal@apdch.ac.in" />
              </div>
              <div>
                <label style={labelStyle}>DCI Report Title</label>
                <input value={letterhead.dciTitle} onChange={e => setLetterhead(p => ({ ...p, dciTitle: e.target.value }))} style={inputStyle} placeholder="e.g. Staff DCI Report" />
              </div>
              <div>
                <label style={labelStyle}>TNMGR University Report Title</label>
                <input value={letterhead.tnmgrTitle} onChange={e => setLetterhead(p => ({ ...p, tnmgrTitle: e.target.value }))} style={inputStyle} placeholder="e.g. Staff Details for TNMGR University" />
              </div>
            </div>

            {/* Live Preview */}
            <div style={{ marginTop: 28, border: "2px dashed #e2e8f0", borderRadius: 12, padding: 24, background: "#f8fafc" }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: "#94a3b8", letterSpacing: 1, marginBottom: 16 }}>PRINT PREVIEW</div>
              <div style={{ display: "flex", alignItems: "center", gap: 20, borderBottom: "2px solid #0f2557", paddingBottom: 16 }}>
                <img src="/apdch_logo.png" alt="Logo" style={{ height: 64 }} />
                <div style={{ textAlign: "center", flex: 1 }}>
                  <div style={{ fontWeight: 900, fontSize: 18, color: "#0f2557" }}>{letterhead.collegeName}</div>
                  <div style={{ fontSize: 14, color: "#64748b", marginTop: 3 }}>{letterhead.address}</div>
                  {(letterhead.phone || letterhead.email) && <div style={{ fontSize: 13, color: "#94a3b8", marginTop: 2 }}>{[letterhead.phone, letterhead.email].filter(Boolean).join(" | ")}</div>}
                </div>
              </div>
              <div style={{ marginTop: 12, display: "flex", gap: 16 }}>
                <div style={{ background: "#eaf1ff", borderRadius: 8, padding: "8px 16px", fontSize: 14, fontWeight: 700, color: "#2563eb" }}>DCI: {letterhead.dciTitle}</div>
                <div style={{ background: "#f0fdf4", borderRadius: 8, padding: "8px 16px", fontSize: 14, fontWeight: 700, color: "#16a34a" }}>TNMGR: {letterhead.tnmgrTitle}</div>
              </div>
            </div>

            <div style={{ marginTop: 24 }}>
              <button
                disabled={savingLetterhead}
                onClick={async () => {
                  setSavingLetterhead(true);
                  try {
                    await api.put("/settings/letterhead", letterhead);
                    showToast("✅ Letterhead saved!");
                  } catch (err) { showToast("❌ Failed: " + err.message); }
                  setSavingLetterhead(false);
                }}
                style={{ background: "linear-gradient(135deg,#1d4ed8,#2563eb)", color: "#fff", border: "none", borderRadius: 12, padding: "12px 32px", fontWeight: 800, fontSize: 16, cursor: savingLetterhead ? "not-allowed" : "pointer", opacity: savingLetterhead ? 0.7 : 1 }}>
                {savingLetterhead ? "⏳ Saving..." : "💾 Save Letterhead"}
              </button>
            </div>
          </div>
        )}

        {/* ── DESIGNATIONS ── */}
        {activeTab === "designations" && (
          <div style={{ background: "#fff", borderRadius: 20, boxShadow: "0 2px 16px rgba(15,37,87,0.08)", overflow: "hidden" }}>
            <div style={{ padding: "22px 28px", borderBottom: "1px solid #f1f5f9" }}>
              <h2 style={{ fontWeight: 800, fontSize: 18, color: "#0f2557", marginBottom: 14 }}>Add New Designation</h2>
              <div style={{ display: "flex", gap: 12 }}>
                <input value={newDesignation} onChange={e => setNewDesignation(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && addDesignation()}
                  placeholder="e.g. Professor, Associate Professor, Assistant Professor..."
                  style={inputStyle} />
                <button onClick={addDesignation} style={{ background: "linear-gradient(135deg,#1d4ed8,#2563eb)", color: "#fff", border: "none", borderRadius: 10, padding: "10px 24px", fontWeight: 800, fontSize: 16, cursor: "pointer" }}>+ Add</button>
              </div>
            </div>
            {loading ? <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>⏳ Loading...</div>
              : designations.length === 0 ? (
                <div style={{ padding: 48, textAlign: "center", color: "#94a3b8" }}>
                  <div style={{ fontSize: 42, marginBottom: 12 }}>🎖️</div>
                  <p>No designations yet. Add one above!</p>
                </div>
              ) : (
                <div style={{ padding: "8px 0" }}>
                  {designations.map((d, i) => (
                    <div key={d.id} style={{ borderBottom: i < designations.length - 1 ? "1px solid #f8fafc" : "none" }}>
                      {editingDesignation?.id === d.id ? (
                        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 28px", background: "#f0f9ff", borderLeft: "3px solid #2563eb" }}>
                          <div style={{ width: 36, height: 36, background: "#eaf1ff", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>🎖️</div>
                          <input
                            value={editingDesignation.name}
                            onChange={e => setEditingDesignation(prev => ({ ...prev, name: e.target.value }))}
                            onKeyDown={e => { if (e.key === "Enter") updateDesignation(); if (e.key === "Escape") setEditingDesignation(null); }}
                            autoFocus
                            style={{ flex: 1, background: "#fff", border: "1.5px solid #93c5fd", borderRadius: 8, padding: "8px 12px", fontSize: 15, outline: "none", fontFamily: "Georgia, serif" }} />
                          <button onClick={updateDesignation} style={{ background: "linear-gradient(135deg,#1d4ed8,#2563eb)", color: "#fff", border: "none", borderRadius: 8, padding: "8px 16px", fontWeight: 700, cursor: "pointer", fontSize: 15, whiteSpace: "nowrap" }}>✅ Save</button>
                          <button onClick={() => setEditingDesignation(null)} style={{ background: "#f1f5f9", color: "#475569", border: "none", borderRadius: 8, padding: "8px 12px", fontWeight: 700, cursor: "pointer", fontSize: 15 }}>✕</button>
                        </div>
                      ) : (
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "13px 28px", transition: "background 0.15s" }}
                          onMouseEnter={e => e.currentTarget.style.background = "#f8fafc"}
                          onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                            <div style={{ width: 36, height: 36, background: "#eaf1ff", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center" }}>🎖️</div>
                            <span style={{ fontWeight: 600, color: "#1e293b", fontSize: 16 }}>{d.name}</span>
                          </div>
                          <div style={{ display: "flex", gap: 8 }}>
                            <button onClick={() => setEditingDesignation({ id: d.id, name: d.name })} style={{ background: "#eaf1ff", color: "#2563eb", border: "none", borderRadius: 8, padding: "6px 14px", fontWeight: 700, cursor: "pointer", fontSize: 15 }}>✏️ Edit</button>
                            <button onClick={() => deleteDesignation(d.id)} style={{ background: "#fef2f2", color: "#dc2626", border: "none", borderRadius: 8, padding: "6px 14px", fontWeight: 700, cursor: "pointer", fontSize: 15 }}>🗑 Delete</button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
          </div>
        )}

        {/* ── EDUCATION OPTIONS ── */}
        {activeTab === "education" && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
            <SimpleListManager collectionName="edu_courses" title="Courses" icon="📘" items={eduCourses} fetchAll={fetchAll} showToast={showToast} placeholder="e.g. BDS, MDS..." />
            <SimpleListManager collectionName="edu_degrees" title="Degrees" icon="🎓" items={eduDegrees} fetchAll={fetchAll} showToast={showToast} placeholder="e.g. Undergraduate, Postgraduate..." />
            <SimpleListManager collectionName="edu_streams" title="Streams" icon="🌊" items={eduStreams} fetchAll={fetchAll} showToast={showToast} placeholder="e.g. Medicine, Dentistry..." />
            <SimpleListManager collectionName="edu_boards" title="Boards / Universities" icon="🏛️" items={eduBoards} fetchAll={fetchAll} showToast={showToast} placeholder="e.g. State Board, TN Dr. M.G.R..." />
            <SimpleListManager collectionName="edu_state_councils" title="State Dental Councils" icon="🛡️" items={eduStateCouncils} fetchAll={fetchAll} showToast={showToast} placeholder="e.g. Tamil Nadu Dental Council..." />
          </div>
        )}

        {/* ── SECTION FIELDS ── */}
        {activeTab === "fields" && (
          <div style={{ display: "flex", gap: 20 }}>

            {/* Left — Section list */}
            <div style={{ width: 220, background: "#fff", borderRadius: 16, boxShadow: "0 2px 12px rgba(15,37,87,0.08)", padding: "12px 0", flexShrink: 0, height: "fit-content" }}>
              <div style={{ padding: "8px 16px 10px", fontSize: 13, fontWeight: 700, color: "#94a3b8", letterSpacing: 1 }}>SECTIONS</div>
              {allSections.map(sec => (
                <div key={sec.key} onClick={() => setActiveSectionKey(sec.key)}
                  style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 16px", cursor: "pointer", borderLeft: activeSectionKey === sec.key ? `3px solid ${sec.color}` : "3px solid transparent", background: activeSectionKey === sec.key ? sec.light : "transparent", color: activeSectionKey === sec.key ? sec.color : "#475569", fontWeight: activeSectionKey === sec.key ? 700 : 400, fontSize: 15, transition: "all 0.15s" }}>
                  <span>{sec.icon}</span>
                  <span style={{ flex: 1, lineHeight: 1.3 }}>{sec.label}</span>
                  <span style={{ background: activeSectionKey === sec.key ? sec.color : "#e2e8f0", color: activeSectionKey === sec.key ? "#fff" : "#64748b", borderRadius: 20, padding: "1px 7px", fontSize: 13, fontWeight: 700 }}>
                    {(sectionFields[sec.key] || []).length}
                  </span>
                </div>
              ))}
            </div>

            {/* Right — Fields manager */}
            <div style={{ flex: 1 }}>
              <div style={{ background: "#fff", borderRadius: 16, boxShadow: "0 2px 12px rgba(15,37,87,0.08)", overflow: "hidden" }}>

                {/* Section header */}
                <div style={{ padding: "18px 24px", borderBottom: "1px solid #f1f5f9", background: activeSectionData?.light, display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ fontSize: 22 }}>{activeSectionData?.icon}</span>
                    <h2 style={{ fontWeight: 800, fontSize: 18, color: "#0f2557" }}>{activeSectionData?.label} — Input Fields</h2>
                  </div>
                </div>

                {/* Add new field form */}
                <div style={{ padding: "18px 24px", borderBottom: "1px solid #f1f5f9", background: "#fafbff" }}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 130px 90px 90px auto", gap: 10, alignItems: "end" }}>
                    <div>
                      <label style={{ fontSize: 13, fontWeight: 700, color: "#374151", display: "block", marginBottom: 5 }}>Field Name *</label>
                      <input value={newField.name} onChange={e => setNewField(f => ({ ...f, name: e.target.value }))}
                        onKeyDown={e => e.key === "Enter" && addField()}
                        placeholder="e.g. Title, Author Name..."
                        style={{ ...inputStyle, flex: "none", width: "100%" }} />
                    </div>
                    <div>
                      <label style={{ fontSize: 13, fontWeight: 700, color: "#374151", display: "block", marginBottom: 5 }}>Type</label>
                      <select value={newField.type} onChange={e => setNewField(f => ({ ...f, type: e.target.value }))}
                        style={{ ...inputStyle, flex: "none", width: "100%", cursor: "pointer" }}>
                        {FIELD_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={{ fontSize: 13, fontWeight: 700, color: "#374151", display: "block", marginBottom: 5 }}>Required?</label>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "10px 0" }}>
                        <input type="checkbox" checked={newField.required} onChange={e => setNewField(f => ({ ...f, required: e.target.checked }))} style={{ width: 15, height: 15 }} />
                        <span style={{ fontSize: 15, color: "#475569" }}>Yes</span>
                      </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "flex-end" }}>
                      <button onClick={addField}
                        style={{ background: "linear-gradient(135deg,#1d4ed8,#2563eb)", color: "#fff", border: "none", borderRadius: 10, padding: "10px 20px", fontWeight: 800, fontSize: 15, cursor: "pointer", whiteSpace: "nowrap" }}>
                        + Add Field
                      </button>
                    </div>
                  </div>
                  {/* Dropdown options input */}
                  {newField.type === "Dropdown" && (
                    <div style={{ marginTop: 10 }}>
                      <label style={{ fontSize: 13, fontWeight: 700, color: "#374151", display: "block", marginBottom: 5 }}>Dropdown Options (comma separated)</label>
                      <input value={newField.options} onChange={e => setNewField(f => ({ ...f, options: e.target.value }))}
                        placeholder="e.g. Sent, Accepted, Published"
                        style={{ ...inputStyle, flex: "none", width: "100%" }} />
                    </div>
                  )}
                </div>

                {/* Fields List */}
                {loading ? (
                  <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>⏳ Loading...</div>
                ) : currentFields.length === 0 ? (
                  <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>
                    <div style={{ fontSize: 38, marginBottom: 10 }}>{activeSectionData?.icon}</div>
                    <p>No fields added yet. Add one above!</p>
                    <p style={{ fontSize: 14, marginTop: 6, color: "#cbd5e1" }}>Faculty will see these fields when adding {activeSectionData?.label}.</p>
                  </div>
                ) : (
                  <>
                    {/* Save Order Button */}
                    {hasSectionFieldsChanged && (
                      <div style={{ padding: "10px 20px", background: "#eaf1ff", borderBottom: "2px solid #2563eb", display: "flex", alignItems: "center", gap: 12, justifyContent: "space-between" }}>
                        <span style={{ fontSize: 14, fontWeight: 700, color: "#2563eb" }}>⚠️ You have unsaved order changes</span>
                        <button onClick={saveSectionFieldsOrder}
                          style={{ background: "linear-gradient(135deg,#1d4ed8,#2563eb)", color: "#fff", border: "none", borderRadius: 8, padding: "8px 18px", fontWeight: 700, cursor: "pointer", fontSize: 14 }}>
                          💾 Save Order
                        </button>
                      </div>
                    )}

                    {/* Table header */}
                    <div style={{ display: "grid", gridTemplateColumns: "40px 1fr 100px 80px 130px", padding: "10px 20px", background: "#f8fafc", borderBottom: "1px solid #f1f5f9" }}>
                      {["Drag", "Field Name", "Type", "Required", "Actions"].map(h => (
                        <span key={h} style={{ fontSize: 13, fontWeight: 700, color: "#64748b", letterSpacing: 0.5 }}>{h}</span>
                      ))}
                    </div>

                    {/* Draggable Fields */}
                    <DragDropContext onDragEnd={handleDragEndSectionFields}>
                      <Droppable droppableId="section-fields">
                        {(provided, snapshot) => (
                          <div {...provided.droppableProps} ref={provided.innerRef}>
                            {currentFields.map((f, i) => (
                              <Draggable key={f.id} draggableId={f.id} index={i}>
                                {(provided, snapshot) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    style={{
                                      ...provided.draggableProps.style,
                                      opacity: snapshot.isDragging ? 0.5 : 1,
                                    }}
                                  >
                                    {editingField?.id === f.id ? (
                                      /* Inline Edit Form */
                                      <div style={{ padding: "14px 20px", background: "#f0f9ff", borderLeft: "3px solid #2563eb", borderBottom: "1px solid #bfdbfe" }}>
                                        <div style={{ display: "grid", gridTemplateColumns: "1fr 140px 90px auto", gap: 10, alignItems: "end" }}>
                                          <div>
                                            <label style={{ fontSize: 13, fontWeight: 700, color: "#374151", display: "block", marginBottom: 4 }}>Field Name</label>
                                            <input value={editingField.name}
                                              onChange={e => setEditingField(prev => ({ ...prev, name: e.target.value }))}
                                              style={{ width: "100%", background: "#fff", border: "1.5px solid #93c5fd", borderRadius: 8, padding: "8px 12px", fontSize: 15, outline: "none", boxSizing: "border-box", fontFamily: "Georgia, serif" }} />
                                          </div>
                                          <div>
                                            <label style={{ fontSize: 13, fontWeight: 700, color: "#374151", display: "block", marginBottom: 4 }}>Type</label>
                                            <select value={editingField.type}
                                              onChange={e => setEditingField(prev => ({ ...prev, type: e.target.value }))}
                                              style={{ width: "100%", background: "#fff", border: "1.5px solid #93c5fd", borderRadius: 8, padding: "8px 10px", fontSize: 15, outline: "none", cursor: "pointer" }}>
                                              {FIELD_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                                            </select>
                                          </div>
                                          <div>
                                            <label style={{ fontSize: 13, fontWeight: 700, color: "#374151", display: "block", marginBottom: 4 }}>Required?</label>
                                            <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "9px 0" }}>
                                              <input type="checkbox" checked={editingField.required}
                                                onChange={e => setEditingField(prev => ({ ...prev, required: e.target.checked }))}
                                                style={{ width: 15, height: 15, cursor: "pointer" }} />
                                              <span style={{ fontSize: 15, color: "#475569" }}>Yes</span>
                                            </div>
                                          </div>
                                          <div style={{ display: "flex", gap: 6 }}>
                                            <button onClick={saveEditField}
                                              style={{ background: "linear-gradient(135deg,#1d4ed8,#2563eb)", color: "#fff", border: "none", borderRadius: 8, padding: "8px 16px", fontWeight: 700, cursor: "pointer", fontSize: 15 }}>✅ Save</button>
                                            <button onClick={() => setEditingField(null)}
                                              style={{ background: "#f1f5f9", color: "#475569", border: "none", borderRadius: 8, padding: "8px 12px", fontWeight: 700, cursor: "pointer", fontSize: 15 }}>✕</button>
                                          </div>
                                        </div>
                                        {editingField.type === "Dropdown" && (
                                          <div style={{ marginTop: 10 }}>
                                            <label style={{ fontSize: 13, fontWeight: 700, color: "#374151", display: "block", marginBottom: 4 }}>Options (comma separated)</label>
                                            <input value={editingField.options}
                                              onChange={e => setEditingField(prev => ({ ...prev, options: e.target.value }))}
                                              placeholder="e.g. Sent, Accepted, Published"
                                              style={{ width: "100%", background: "#fff", border: "1.5px solid #93c5fd", borderRadius: 8, padding: "8px 12px", fontSize: 15, outline: "none", boxSizing: "border-box", fontFamily: "Georgia, serif" }} />
                                          </div>
                                        )}
                                      </div>
                                    ) : (
                                      /* Normal Row with Drag Handle */
                                      <div style={{ display: "grid", gridTemplateColumns: "40px 1fr 100px 80px 130px", padding: "12px 20px", borderBottom: i < currentFields.length - 1 ? "1px solid #f8fafc" : "none", alignItems: "center", transition: "background 0.15s", background: snapshot.isDragging ? "#e0f2fe" : "transparent" }}
                                        onMouseEnter={e => !snapshot.isDragging && (e.currentTarget.style.background = "#f8fafc")}
                                        onMouseLeave={e => !snapshot.isDragging && (e.currentTarget.style.background = "transparent")}>
                                        
                                        {/* Drag Handle */}
                                        <div {...provided.dragHandleProps} style={{ cursor: "grab", fontSize: 18, userSelect: "none", textAlign: "center" }}>
                                          ⠿
                                        </div>

                                        {/* Field Name & Details */}
                                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                          <div style={{ width: 30, height: 30, background: activeSectionData?.light, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15 }}>
                                            {f.type === "Number" ? "🔢" : f.type === "Date" ? "📅" : f.type === "Month-Year" ? "📆" : f.type === "Dropdown" ? "🔽" : f.type === "Textarea" ? "📝" : f.type === "Image" ? "🖼️" : "✏️"}
                                          </div>
                                          <div>
                                            <span style={{ fontWeight: 600, color: "#1e293b", fontSize: 15 }}>{f.name}</span>
                                            {f.required && <span style={{ background: "#fef3c7", color: "#d97706", borderRadius: 20, padding: "1px 7px", fontSize: 13, fontWeight: 700, marginLeft: 6 }}>Required</span>}
                                            {f.options?.length > 0 && <div style={{ fontSize: 13, color: "#94a3b8", marginTop: 2 }}>Options: {Array.isArray(f.options) ? f.options.join(", ") : f.options}</div>}
                                          </div>
                                        </div>

                                        {/* Type */}
                                        <span style={{ fontSize: 14, color: "#64748b" }}>{f.type}</span>

                                        {/* Required */}
                                        <span style={{ fontSize: 14, color: f.required ? "#16a34a" : "#94a3b8", fontWeight: 600 }}>{f.required ? "✅ Yes" : "No"}</span>

                                        {/* Actions */}
                                        <div style={{ display: "flex", gap: 6 }}>
                                          <button onClick={() => setEditingField({ id: f.id, name: f.name, type: f.type, required: f.required, options: Array.isArray(f.options) ? f.options.join(", ") : (f.options || "") })}
                                            style={{ background: "#eaf1ff", color: "#2563eb", border: "none", borderRadius: 8, padding: "5px 10px", fontWeight: 700, cursor: "pointer", fontSize: 14 }}>✏️</button>
                                          <button onClick={() => deleteField(f.id)}
                                            style={{ background: "#fef2f2", color: "#dc2626", border: "none", borderRadius: 8, padding: "5px 10px", fontWeight: 700, cursor: "pointer", fontSize: 14 }}>🗑</button>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </Draggable>
                            ))}
                            {provided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    </DragDropContext>
                  </>
                )}
              </div>

              <div style={{ background: "#eaf1ff", borderRadius: 12, padding: "12px 18px", marginTop: 14, display: "flex", alignItems: "center", gap: 10 }}>
                <span>ℹ️</span>
                <span style={{ fontSize: 14, color: "#1d4ed8", fontWeight: 600 }}>
                  Fields added here will appear when faculty members add {activeSectionData?.label} entries.
                </span>
              </div>
            </div>
          </div>
        )}

        {/* ── MANAGE SECTIONS (Superadmin only) ── */}
        {activeTab === "sections" && isSuperAdmin && (
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

            {/* Create new section */}
            <div style={{ background: "#fff", borderRadius: 20, boxShadow: "0 2px 16px rgba(15,37,87,0.08)", overflow: "hidden" }}>
              <div style={{ padding: "22px 28px", borderBottom: "1px solid #f1f5f9" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
                  <div style={{ width: 36, height: 36, background: "linear-gradient(135deg,#0f2557,#2563eb)", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>➕</div>
                  <div>
                    <h2 style={{ fontWeight: 800, fontSize: 18, color: "#0f2557", margin: 0 }}>Create New Section</h2>
                    <p style={{ fontSize: 14, color: "#94a3b8", margin: 0, marginTop: 2 }}>Add a custom section visible to all faculty members</p>
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 200px 1fr auto", gap: 12, alignItems: "end" }}>
                  <div>
                    <label style={{ fontSize: 13, fontWeight: 700, color: "#374151", display: "block", marginBottom: 5 }}>Section Name *</label>
                    <input
                      value={newSection.label}
                      onChange={e => setNewSection(s => ({ ...s, label: e.target.value }))}
                      onKeyDown={e => e.key === "Enter" && addCustomSection()}
                      placeholder="e.g. Industry Visits, Consultancy..."
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: 13, fontWeight: 700, color: "#374151", display: "block", marginBottom: 5 }}>Icon</label>
                    <select
                      value={newSection.icon}
                      onChange={e => setNewSection(s => ({ ...s, icon: e.target.value }))}
                      style={{ ...inputStyle, cursor: "pointer" }}
                    >
                      {SECTION_ICONS.map(ic => <option key={ic} value={ic}>{ic}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: 13, fontWeight: 700, color: "#374151", display: "block", marginBottom: 5 }}>Color Theme</label>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", paddingTop: 6 }}>
                      {SECTION_COLORS.map((c, i) => (
                        <div key={i} onClick={() => setNewSection(s => ({ ...s, colorIndex: i }))}
                          style={{ width: 24, height: 24, borderRadius: "50%", background: c.color, cursor: "pointer", border: newSection.colorIndex === i ? `3px solid #0f2557` : "3px solid transparent", boxSizing: "border-box", transition: "border 0.15s" }}
                        />
                      ))}
                    </div>
                  </div>
                  <button onClick={editingSection ? updateCustomSection : addCustomSection}
                    style={{ background: "linear-gradient(135deg,#0f2557,#2563eb)", color: "#fff", border: "none", borderRadius: 10, padding: "10px 24px", fontWeight: 800, fontSize: 16, cursor: "pointer", whiteSpace: "nowrap", alignSelf: "end" }}>
                    {editingSection ? "✅ Save Changes" : "✨ Create Section"}
                  </button>
                  {editingSection && (
                    <button onClick={() => setEditingSection(null)}
                      style={{ background: "#f1f5f9", color: "#475569", border: "none", borderRadius: 10, padding: "10px 20px", fontWeight: 700, fontSize: 16, cursor: "pointer", alignSelf: "end" }}>
                      ✕ Cancel
                    </button>
                  )}
                </div>

                {/* Preview */}
                {newSection.label.trim() && (
                  <div style={{ marginTop: 14, padding: "10px 16px", background: SECTION_COLORS[newSection.colorIndex % SECTION_COLORS.length].light, borderRadius: 10, display: "flex", alignItems: "center", gap: 10, borderLeft: `3px solid ${SECTION_COLORS[newSection.colorIndex % SECTION_COLORS.length].color}` }}>
                    <span style={{ fontSize: 22 }}>{newSection.icon}</span>
                    <span style={{ fontWeight: 700, fontSize: 16, color: SECTION_COLORS[newSection.colorIndex % SECTION_COLORS.length].color }}>Preview: {newSection.label}</span>
                    <span style={{ fontSize: 13, color: "#94a3b8", marginLeft: 8 }}>Key: {newSection.label.toLowerCase().replace(/[^a-z0-9 ]/g, "").trim().replace(/ +/g, "_").replace(/_+(\w)/g, (_, c) => c.toUpperCase()).replace(/^(\w)/, (_, c) => c.toLowerCase())}</span>
                  </div>
                )}
              </div>

              {/* Info */}
              <div style={{ padding: "12px 28px", background: "#fafbff" }}>
                <span style={{ fontSize: 14, color: "#64748b" }}>ℹ️ After creating a section, go to <strong>📋 Section Fields</strong> tab to add input fields for it.</span>
              </div>
            </div>

            {/* Built-in sections (read-only) */}
            <div style={{ background: "#fff", borderRadius: 20, boxShadow: "0 2px 16px rgba(15,37,87,0.08)", overflow: "hidden" }}>
              <div style={{ padding: "16px 28px", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 18 }}>🔒</span>
                <h2 style={{ fontWeight: 800, fontSize: 17, color: "#0f2557", margin: 0 }}>Built-in Sections</h2>
                <span style={{ background: "#f1f5f9", color: "#64748b", borderRadius: 20, padding: "2px 10px", fontSize: 13, fontWeight: 700 }}>{SECTIONS.length} sections</span>
              </div>
              {SECTIONS.map((sec, i) => (
                <div key={sec.key} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 28px", borderBottom: i < SECTIONS.length - 1 ? "1px solid #f8fafc" : "none" }}
                  onMouseEnter={e => e.currentTarget.style.background = "#f8fafc"}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{ width: 36, height: 36, background: sec.light, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, border: `2px solid ${sec.color}30` }}>{sec.icon}</div>
                    <div>
                      <span style={{ fontWeight: 600, color: "#1e293b", fontSize: 16 }}>{sec.label}</span>
                      <div style={{ fontSize: 13, color: "#94a3b8", marginTop: 1 }}>Key: {sec.key}</div>
                    </div>
                  </div>
                  <span style={{ background: "#f1f5f9", color: "#94a3b8", borderRadius: 8, padding: "4px 12px", fontSize: 13, fontWeight: 700 }}>Built-in</span>
                </div>
              ))}
            </div>

            {/* Custom sections */}
            {customSections.length > 0 && (
              <div style={{ background: "#fff", borderRadius: 20, boxShadow: "0 2px 16px rgba(15,37,87,0.08)", overflow: "hidden" }}>
                <div style={{ padding: "16px 28px", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 18 }}>✨</span>
                  <h2 style={{ fontWeight: 800, fontSize: 17, color: "#0f2557", margin: 0 }}>Custom Sections</h2>
                  <span style={{ background: "#eaf1ff", color: "#2563eb", borderRadius: 20, padding: "2px 10px", fontSize: 13, fontWeight: 700 }}>{customSections.length} created</span>
                </div>
                {customSections.map((sec, i) => (
                  <div key={sec.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 28px", borderBottom: i < customSections.length - 1 ? "1px solid #f8fafc" : "none" }}
                    onMouseEnter={e => e.currentTarget.style.background = "#f8fafc"}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <div style={{ width: 36, height: 36, background: sec.light, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, border: `2px solid ${sec.color}30` }}>{sec.icon}</div>
                      <div>
                        <span style={{ fontWeight: 600, color: "#1e293b", fontSize: 16 }}>{sec.label}</span>
                        <div style={{ fontSize: 13, color: "#94a3b8", marginTop: 1 }}>Key: {sec.key}</div>
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button onClick={() => setEditingSection({ ...sec, colorIndex: SECTION_COLORS.findIndex(c => c.color === sec.color) })}
                        style={{ background: "#eaf1ff", color: "#2563eb", border: "none", borderRadius: 8, padding: "6px 14px", fontWeight: 700, cursor: "pointer", fontSize: 15 }}>✏️ Edit</button>
                      <button onClick={() => deleteCustomSection(sec.id, sec.key)}
                        style={{ background: "#fef2f2", color: "#dc2626", border: "none", borderRadius: 8, padding: "6px 14px", fontWeight: 700, cursor: "pointer", fontSize: 15 }}>🗑 Delete</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── FACULTY PROFILE FIELDS ── */}
        {activeTab === "facultyFields" && (
          <div style={{ background: "#fff", borderRadius: 20, boxShadow: "0 2px 16px rgba(15,37,87,0.08)", overflow: "hidden" }}>
            <div style={{ padding: "18px 24px", borderBottom: "1px solid #f1f5f9", background: "#f8fafc" }}>
              <h2 style={{ fontWeight: 800, fontSize: 18, color: "#0f2557", margin: 0 }}>👤 Faculty Profile Fields</h2>
              <p style={{ fontSize: 14, color: "#64748b", marginTop: 4 }}>These fields will appear when creating a faculty and in their profile settings.</p>
            </div>

            {/* Add new field form */}
            <div style={{ padding: "18px 24px", borderBottom: "1px solid #f1f5f9", background: "#fafbff" }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 130px 140px 90px auto", gap: 10, alignItems: "end" }}>
                <div>
                  <label style={{ fontSize: 13, fontWeight: 700, color: "#374151", display: "block", marginBottom: 5 }}>Field Name *</label>
                  <input value={newFacultyField.name} onChange={e => setNewFacultyField(f => ({ ...f, name: e.target.value }))}
                    placeholder="e.g. Qualification..."
                    style={{ ...inputStyle, width: "100%" }} />
                </div>
                <div>
                  <label style={{ fontSize: 13, fontWeight: 700, color: "#374151", display: "block", marginBottom: 5 }}>Type</label>
                  <select value={newFacultyField.type} onChange={e => setNewFacultyField(f => ({ ...f, type: e.target.value }))}
                    style={{ ...inputStyle, width: "100%", cursor: "pointer" }}>
                    {FIELD_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: 13, fontWeight: 700, color: "#374151", display: "block", marginBottom: 5 }}>Group/Section</label>
                  <input value={newFacultyField.group} onChange={e => setNewFacultyField(f => ({ ...f, group: e.target.value }))}
                    placeholder="e.g. Staff Details"
                    style={{ ...inputStyle, width: "100%" }} />
                </div>
                <div>
                  <label style={{ fontSize: 13, fontWeight: 700, color: "#374151", display: "block", marginBottom: 5 }}>Required?</label>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "10px 0" }}>
                    <input type="checkbox" checked={newFacultyField.required} onChange={e => setNewFacultyField(f => ({ ...f, required: e.target.checked }))} style={{ width: 15, height: 15 }} />
                    <span style={{ fontSize: 15, color: "#475569" }}>Yes</span>
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "flex-end" }}>
                  <button onClick={addFacultyField}
                    style={{ background: "linear-gradient(135deg,#1d4ed8,#2563eb)", color: "#fff", border: "none", borderRadius: 10, padding: "10px 20px", fontWeight: 800, fontSize: 15, cursor: "pointer", whiteSpace: "nowrap" }}>
                    + Add Field
                  </button>
                </div>
              </div>
              {newFacultyField.type === "Dropdown" && (
                <div style={{ marginTop: 10 }}>
                  <label style={{ fontSize: 13, fontWeight: 700, color: "#374151", display: "block", marginBottom: 5 }}>Dropdown Options (comma separated)</label>
                  <input value={newFacultyField.options} onChange={e => setNewFacultyField(f => ({ ...f, options: e.target.value }))}
                    placeholder="e.g. BDS, MDS, PhD"
                    style={{ ...inputStyle, width: "100%" }} />
                </div>
              )}
            </div>

            {/* Fields List */}
            {loading ? (
              <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>⏳ Loading...</div>
            ) : facultyFields.length === 0 ? (
              <div style={{ padding: 60, textAlign: "center", color: "#94a3b8" }}>
                <div style={{ fontSize: 42, marginBottom: 12 }}>👤</div>
                <p>No profile fields added yet.</p>
              </div>
            ) : (
              <div style={{ padding: 0 }}>
                {/* Save Order Button */}
                {hasFacultyFieldsChanged && (
                  <div style={{ padding: "10px 20px", background: "#eaf1ff", borderBottom: "2px solid #2563eb", display: "flex", alignItems: "center", gap: 12, justifyContent: "space-between" }}>
                    <span style={{ fontSize: 14, fontWeight: 700, color: "#2563eb" }}>⚠️ You have unsaved order changes</span>
                    <button onClick={saveFacultyFieldsOrder}
                      style={{ background: "linear-gradient(135deg,#1d4ed8,#2563eb)", color: "#fff", border: "none", borderRadius: 8, padding: "8px 18px", fontWeight: 700, cursor: "pointer", fontSize: 14 }}>
                      💾 Save Order
                    </button>
                  </div>
                )}

                {/* Table header */}
                <div style={{ display: "grid", gridTemplateColumns: "40px 1fr 120px 100px 80px 130px", padding: "10px 20px", background: "#f8fafc", borderBottom: "1px solid #f1f5f9" }}>
                  {["Drag", "Field Name", "Group", "Type", "Required", "Actions"].map(h => (
                    <span key={h} style={{ fontSize: 13, fontWeight: 700, color: "#64748b", letterSpacing: 0.5 }}>{h}</span>
                  ))}
                </div>

                {/* Draggable Faculty Fields */}
                <DragDropContext onDragEnd={handleDragEndFacultyFields}>
                  <Droppable droppableId="faculty-fields">
                    {(provided, snapshot) => (
                      <div {...provided.droppableProps} ref={provided.innerRef}>
                        {facultyFields.map((f, i) => (
                          <Draggable key={f.id} draggableId={f.id} index={i}>
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                style={{
                                  ...provided.draggableProps.style,
                                  opacity: snapshot.isDragging ? 0.5 : 1,
                                }}
                              >
                                <div style={{ display: "grid", gridTemplateColumns: "40px 1fr 120px 100px 80px 130px", padding: "12px 20px", borderBottom: i < facultyFields.length - 1 ? "1px solid #f8fafc" : "none", alignItems: "center", background: snapshot.isDragging ? "#e0f2fe" : "transparent", transition: "background 0.15s" }}
                                  onMouseEnter={e => !snapshot.isDragging && (e.currentTarget.style.background = "#f8fafc")}
                                  onMouseLeave={e => !snapshot.isDragging && (e.currentTarget.style.background = "transparent")}>
                                  
                                  {/* Drag Handle */}
                                  <div {...provided.dragHandleProps} style={{ cursor: "grab", fontSize: 18, userSelect: "none", textAlign: "center" }}>
                                    ⠿
                                  </div>

                                  {/* Field Name */}
                                  <span style={{ fontWeight: 600, color: "#1e293b", fontSize: 15 }}>{f.name}</span>

                                  {/* Group */}
                                  <span style={{ fontSize: 14, color: "#64748b" }}>{f.group || "General Info"}</span>

                                  {/* Type */}
                                  <span style={{ fontSize: 14, color: "#64748b" }}>{f.type}</span>

                                  {/* Required */}
                                  <span>{f.required ? "✅" : "—"}</span>

                                  {/* Actions */}
                                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                                    <button onClick={() => setEditingFacultyField({ id: f.id, name: f.name, type: f.type, required: f.required, group: f.group || "", options: Array.isArray(f.options) ? f.options.join(", ") : (f.options || ""), isSystem: f.isSystem })}
                                      style={{ background: "#eaf1ff", color: "#2563eb", border: "none", borderRadius: 8, padding: "5px 10px", fontWeight: 700, cursor: "pointer", fontSize: 13 }}>✏️</button>
                                    {!f.isSystem && (
                                      <button onClick={() => deleteFacultyField(f.id)} style={{ background: "#fef2f2", color: "#dc2626", border: "none", borderRadius: 8, padding: "5px 10px", fontWeight: 700, cursor: "pointer", fontSize: 13 }}>🗑</button>
                                    )}
                                    {f.isSystem && <span style={{ fontSize: 13, color: "#94a3b8", fontWeight: 600 }}>System</span>}
                                  </div>
                                </div>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              </div>
            )}
            
            {/* Inline editing for faculty fields */}
            {editingFacultyField && (
              <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
                <div style={{ background: "#fff", borderRadius: 20, padding: 32, width: "100%", maxWidth: 500 }}>
                  <h3 style={{ margin: "0 0 20px", color: "#0f2557" }}>Edit Faculty Field</h3>
                  <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                    <div>
                      <label style={labelStyle}>Field Name</label>
                      <input value={editingFacultyField.name} 
                        onChange={e => setEditingFacultyField(p => ({ ...p, name: e.target.value }))}
                        style={inputStyle} />
                    </div>
                    <div>
                      <label style={labelStyle}>Group/Section</label>
                      <input value={editingFacultyField.group} 
                        onChange={e => setEditingFacultyField(p => ({ ...p, group: e.target.value }))}
                        style={inputStyle} placeholder="e.g. Job Details" />
                    </div>
                    {!editingFacultyField.isSystem && (
                      <div>
                        <label style={labelStyle}>Type</label>
                        <select value={editingFacultyField.type} 
                          onChange={e => setEditingFacultyField(p => ({ ...p, type: e.target.value }))}
                          style={inputStyle}>
                          {FIELD_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </div>
                    )}
                    <div>
                      <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                        <input type="checkbox" checked={editingFacultyField.required} 
                          onChange={e => setEditingFacultyField(p => ({ ...p, required: e.target.checked }))} />
                        <span style={{ fontSize: 16, fontWeight: 700 }}>Required?</span>
                      </label>
                    </div>
                    {editingFacultyField.type === "Dropdown" && (
                      <div>
                        <label style={labelStyle}>Options (comma separated)</label>
                        <input value={editingFacultyField.options} 
                          onChange={e => setEditingFacultyField(p => ({ ...p, options: e.target.value }))}
                          style={inputStyle} />
                      </div>
                    )}
                  </div>
                  <div style={{ display: "flex", gap: 12, marginTop: 24 }}>
                    <button onClick={() => setEditingFacultyField(null)} style={{ flex: 1, padding: 12, borderRadius: 10, border: "none", background: "#f1f5f9", cursor: "pointer" }}>Cancel</button>
                    <button onClick={saveEditFacultyField} style={{ flex: 1, padding: 12, borderRadius: 10, border: "none", background: "#2563eb", color: "#fff", fontWeight: 700, cursor: "pointer" }}>Save Changes</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
}

function SimpleListManager({ collectionName, title, icon, items, fetchAll, showToast, placeholder }) {
  const [newItem, setNewItem] = useState("");
  const [editingItem, setEditingItem] = useState(null);
  
  const inputStyle = { flex: 1, background: "#f8fafc", border: "1.5px solid #e2e8f0", borderRadius: 10, padding: "10px 14px", fontSize: 15, outline: "none", fontFamily: "Georgia, serif", color: "#1e293b", boxSizing: "border-box" };

  async function handleAdd() {
    if (!newItem.trim()) return;
    try {
      await api.post(`/${collectionName}`, { name: newItem.trim(), createdAt: new Date().toISOString() });
      setNewItem(""); fetchAll(); showToast(`✅ Added`);
    } catch (err) { console.error(err); showToast("❌ Failed: " + err.message); }
  }

  async function handleDelete(id) {
    if (!window.confirm("Delete this item?")) return;
    try { 
      await api.delete(`/${collectionName}/${id}`); fetchAll(); showToast("✅ Deleted"); 
    } catch (err) { console.error(err); showToast("❌ Failed: " + err.message); }
  }

  async function handleUpdate() {
    if (!editingItem?.name?.trim()) return;
    try {
      await api.put(`/${collectionName}/${editingItem.id}`, { name: editingItem.name.trim() });
      setEditingItem(null); fetchAll(); showToast("✅ Updated");
    } catch (err) { console.error(err); showToast("❌ Failed: " + err.message); }
  }

  return (
    <div style={{ background: "#fff", borderRadius: 20, boxShadow: "0 2px 16px rgba(15,37,87,0.08)", overflow: "hidden", display: "flex", flexDirection: "column", maxHeight: 400 }}>
      <div style={{ padding: "18px 24px", borderBottom: "1px solid #f1f5f9" }}>
        <h2 style={{ fontWeight: 800, fontSize: 17, color: "#0f2557", marginBottom: 12, display: "flex", gap: 8, alignItems: "center" }}><span>{icon}</span> {title}</h2>
        <div style={{ display: "flex", gap: 8 }}>
          <input value={newItem} onChange={e => setNewItem(e.target.value)} onKeyDown={e => e.key === "Enter" && handleAdd()} placeholder={placeholder} style={inputStyle} />
          <button onClick={handleAdd} style={{ background: "linear-gradient(135deg,#1d4ed8,#2563eb)", color: "#fff", border: "none", borderRadius: 10, padding: "8px 16px", fontWeight: 800, fontSize: 15, cursor: "pointer", whiteSpace: "nowrap" }}>+ Add</button>
        </div>
      </div>
      <div style={{ overflowY: "auto", padding: "8px 0", flex: 1 }}>
        {items.length === 0 ? <div style={{ padding: 20, textAlign: "center", color: "#94a3b8", fontSize: 15 }}>No items yet.</div> : 
          items.map((item, i) => (
            <div key={item.id} style={{ borderBottom: i < items.length - 1 ? "1px solid #f8fafc" : "none" }}>
              {editingItem?.id === item.id ? (
                <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 24px", background: "#f0f9ff", borderLeft: "3px solid #2563eb" }}>
                  <input value={editingItem.name} onChange={e => setEditingItem(prev => ({ ...prev, name: e.target.value }))} onKeyDown={e => { if (e.key === "Enter") handleUpdate(); if (e.key === "Escape") setEditingItem(null); }} autoFocus style={{ ...inputStyle, padding: "6px 10px", fontSize: 14 }} />
                  <button onClick={handleUpdate} style={{ background: "linear-gradient(135deg,#1d4ed8,#2563eb)", color: "#fff", border: "none", borderRadius: 6, padding: "6px 12px", fontWeight: 700, cursor: "pointer", fontSize: 14 }}>✅</button>
                  <button onClick={() => setEditingItem(null)} style={{ background: "#f1f5f9", color: "#475569", border: "none", borderRadius: 6, padding: "6px 12px", fontWeight: 700, cursor: "pointer", fontSize: 14 }}>✕</button>
                </div>
              ) : (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 24px", transition: "background 0.15s" }} onMouseEnter={e => e.currentTarget.style.background = "#f8fafc"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ width: 28, height: 28, background: "#eaf1ff", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>{icon}</div>
                    <span style={{ fontWeight: 600, color: "#1e293b", fontSize: 15 }}>{item.name}</span>
                  </div>
                  <div style={{ display: "flex", gap: 6 }}>
                    <button onClick={() => setEditingItem({ id: item.id, name: item.name })} style={{ background: "#eaf1ff", color: "#2563eb", border: "none", borderRadius: 6, padding: "4px 10px", fontWeight: 700, cursor: "pointer", fontSize: 14 }}>✏️</button>
                    <button onClick={() => handleDelete(item.id)} style={{ background: "#fef2f2", color: "#dc2626", border: "none", borderRadius: 6, padding: "4px 10px", fontWeight: 700, cursor: "pointer", fontSize: 14 }}>🗑</button>
                  </div>
                </div>
              )}
            </div>
          ))}
      </div>
    </div>
  );
}

